import { Id, toast } from 'react-toastify';
import * as filestack from 'filestack-js';
import { brandKitVerifyFontSupportCreate } from '@goldcast/api/content';
import { getBrandKitUrlPure } from '@goldcast/player-config/src/utils/fontUtils';
import { BRAND_KIT_MAX_FILE_SIZE, FONT_VALIDATION_ERROR_OTF, FONT_VALIDATION_ERROR_TTF } from '../constants';
import appLogger from '@/services/logger';
import ProgressToast from '@/Pages/Sessions/NewContentModal/ProgressToast';
import { getFileExtension, getFileSize, getFileSizeInMb } from '@/libs/file';
import { currentUser } from '@/stores/user';
import { PROGRESS_TOAST_PARAMS } from '@/libs/toast/constants';
import { MediaPickerTab } from '@/Pages/BrandKit/types';
import Icon from '@/components/atoms/Icon';

type BrandKitMediaItem = { url: string };

export function getUploadConfig(
  uploadToastId: React.MutableRefObject<Id | undefined>,
  totalSize: number
): filestack.UploadOptions {
  const filesSize = getFileSize(totalSize);
  return {
    retry: 3,
    timeout: 60000,
    onProgress: p => {
      if (uploadToastId.current) {
        toast.update(uploadToastId.current, {
          render: getUploadToastContent(p.totalPercent, p.totalBytes, filesSize)
        });
      } else {
        uploadToastId.current = toast.info(getUploadToastContent(0, 0, filesSize), {
          ...PROGRESS_TOAST_PARAMS,
          icon: (
            <Icon
              name="IconLoader2"
              className="tw-animate-spin"
              style={{ animationDuration: '2000ms' }}
              size={17}
              stroke={1.5}
            />
          )
        });
      }
    }
  };
}

export function getUploadToastContent(progress: number, bytes: number, filesSize: string): JSX.Element {
  return <ProgressToast progressPercentage={progress} progressBytes={bytes} totalSize={filesSize} />;
}

export function getFilesForUpload(key: string, files: File[], maxFileSize: number = BRAND_KIT_MAX_FILE_SIZE): File[] {
  return files
    .filter(file => getFileSizeInMb(file.size) < maxFileSize)
    .map(file => {
      // We are using timestamp to prevent overriding of brand kit files by having same name files
      const timestamp = new Date().getTime();
      const fileName = filestack.sanitizeName(`${key}/${timestamp}/${encodeUploadBrandKitFileName(file.name)}`);
      var blob = file.slice(0, file.size, file.type);
      return new File([blob], fileName, { type: file.type });
    });
}

export function getBrandKitUrl(fileName: string, clipId?: string): string {
  return getBrandKitUrlPure({ fileName, clipId, organization: currentUser.getSnapshot()?.organization });
}

export function loadBrandKitFonts(fontsMap: Record<string, string>) {
  Object.keys(fontsMap).forEach(fontUrl => {
    const fontFace = new FontFace(fontsMap[fontUrl], `url(${fontUrl})`);
    // Load the font dynamically
    fontFace
      .load()
      .then(loadedFont => {
        document.fonts.add(loadedFont);
      })
      .catch(err => {
        appLogger.error('Error loading brand kit font', err);
      });
  });
}

export function verifyFontFile(fontFileName: string): Promise<any> {
  return brandKitVerifyFontSupportCreate({
    body: { font_location: getBrandKitUrl(fontFileName) }
  })
    .then(res => {
      if (!res?.font_supported) {
        const errorMessage =
          getFileExtension(fontFileName) === '.ttf' ? FONT_VALIDATION_ERROR_TTF : FONT_VALIDATION_ERROR_OTF;
        return Promise.reject({
          message: errorMessage
        });
      }
      return Promise.resolve();
    })
    .catch(err => Promise.reject(err?.message));
}

/**
 * To be used only for Brand kit upload
 * Do not use this for fetching the Brand kit uploaded files otherwise will break the file fetching,
 * Should use encodeBrandKitFileName for fetching files.
 */
function encodeUploadBrandKitFileName(fileName: string): string {
  const extension = getFileExtension(fileName);
  const allTextWithoutExtension = fileName.slice(0, -extension.length);
  const sanitizeFileName = allTextWithoutExtension.replace(/[^a-zA-Z0-9./_-]/g, '').replace(/\s+/g, '_');
  const sanitizeFileNameWithFallback = sanitizeFileName === '.' ? 'Untitled.' : sanitizeFileName;
  return `${sanitizeFileNameWithFallback}${extension}`;
}

export function getFileName(url: string): string {
  return decodeURIComponent(url.split('/').pop()?.split('?')[0] ?? url);
}

export function isSelectedUrlInBrandKitMedia(
  selectedUrl: string | undefined,
  brandKitMedia: BrandKitMediaItem[]
): boolean {
  if (!selectedUrl) {
    return false;
  }

  return brandKitMedia.some(item => item.url === selectedUrl);
}

/**
 * Disables confirm when nothing is selected, when the selection is not in the
 * active brand kit, or when on Uploads tab the selection is not a recently
 * uploaded / recently used item.
 */
export function getMediaPickerConfirmDisabled({
  selectedUrl,
  activeTab,
  brandKitMedia,
  uploadsMediaUrls = []
}: {
  selectedUrl: string | undefined;
  activeTab: MediaPickerTab;
  brandKitMedia: BrandKitMediaItem[];
  uploadsMediaUrls?: string[];
}): boolean {
  if (!selectedUrl) {
    return true;
  }

  if (isSelectedUrlInBrandKitMedia(selectedUrl, brandKitMedia)) {
    return false;
  }

  if (activeTab === 'uploads' && uploadsMediaUrls.includes(selectedUrl)) {
    return false;
  }

  return true;
}
